import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Loader2, MapPin, CalendarDays, BedDouble, Wallet, Briefcase, Clock } from "lucide-react";
import { API } from "../../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { STATUS_TONE, fmtDate, fmtBudget } from "./portalStatus";

const Row = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 py-2.5">
    <Icon className="w-4 h-4 text-[#C8A96B] mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-900" data-testid={testId}>{value || "—"}</div>
    </div>
  </div>
);

export const PortalReferralDetail = ({ token, referralId, onClose }) => {
  const [ref, setRef] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!referralId) return;
    setState("loading"); setRef(null);
    axios.get(`${API}/portal/${token}/referrals/${referralId}`)
      .then((r) => { setRef(r.data); setState("ok"); })
      .catch(() => setState("error"));
  }, [token, referralId]);

  return (
    <Dialog open={!!referralId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="portal-referral-detail">
        <DialogHeader>
          <DialogTitle className="text-[#0A2342] font-['Playfair_Display'] text-xl">
            {ref ? ref.referral_name : "Referral"}
          </DialogTitle>
        </DialogHeader>
        {state === "loading" && <div className="py-10 flex justify-center" data-testid="portal-detail-loading"><Loader2 className="w-6 h-6 animate-spin text-[#C8A96B]" /></div>}
        {state === "error" && <p className="py-8 text-center text-sm text-red-600" data-testid="portal-detail-error">We couldn't load this referral. Please refresh and try again.</p>}
        {state === "ok" && ref && (
          <div>
            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_TONE[ref.status] || ""}`} data-testid="portal-detail-status">{ref.status_label}</span>

            <ol className="mt-5 space-y-2" data-testid="portal-detail-progress">
              {ref.progress_steps.map((label, i) => {
                const done = ref.progress_step >= i;
                const current = ref.progress_step === i && !ref.lease_signed;
                return (
                  <li key={label} className="flex items-center gap-3 text-sm">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${done ? "bg-[#0A2342] border-[#0A2342] text-white" : "border-slate-300 text-slate-400"}`}>
                      {done && !current ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className={done ? "text-slate-900 font-medium" : "text-slate-400"}>{label}</span>
                    {current && <span className="text-[10px] uppercase tracking-wide text-[#C8A96B] font-bold">Current</span>}
                  </li>
                );
              })}
              {ref.status === "lost" && <li className="text-xs text-slate-500 pl-9">This referral has been closed.</li>}
            </ol>

            <div className="mt-5 rounded-xl bg-[#FAFAF7] border border-[#E8E4D8] p-4">
              <div className="text-[11px] uppercase tracking-wide text-[#C8A96B] font-bold">Next step</div>
              <p className="text-sm text-slate-800 mt-1" data-testid="portal-detail-next-step">{ref.next_step}</p>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 divide-y sm:divide-y-0 divide-slate-100">
              <Row icon={CalendarDays} label="Move date" value={ref.move_date} testId="portal-detail-move-date" />
              <Row icon={MapPin} label="Preferred areas" value={ref.preferred_neighborhood} testId="portal-detail-areas" />
              <Row icon={BedDouble} label="Bedrooms" value={ref.bedrooms} testId="portal-detail-bedrooms" />
              <Row icon={Wallet} label="Budget" value={fmtBudget(ref.budget)} testId="portal-detail-budget" />
              <Row icon={Briefcase} label="Department" value={ref.department} testId="portal-detail-department" />
              <Row icon={Clock} label="Last update" value={fmtDate(ref.updated_at)} testId="portal-detail-updated" />
            </div>
            {ref.apartment_preferences && (
              <p className="mt-2 text-xs text-slate-500"><span className="font-semibold text-slate-600">Your notes:</span> {ref.apartment_preferences}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
